import { getVocabulary } from 'components/Vocabulary';
import { ARMORCODE_GROUP_KEY } from 'utils/armorcodeGroupUtil';
import { find } from 'utils/lodashUtils';
import { CellRenderers } from 'utils/tableUtils';

export const MULTI_SORT_COLUMNS_LIMIT = 5;

export const CRA_DUE_DATE_KEYS = [
  'craEarlyWarningDue',
  'craNotificationDue',
  'craFinalReportDue',
  'craUserDisclosureDue',
];

export const DATE_FILTER_API_SUPPORT = [
  ...CRA_DUE_DATE_KEYS,
  'foundOn',
  'lastModifiedDate',
  'lastUpdated',
  'issueResolved',
  'unsupressTime',
  'publishedDate',
  'lastTriagedDate',
  'lastSuppressDate',
  'lastConfirmDate',
  'lastFalsePositiveDate',
  'lastAcceptRiskDate',
  'lastOpenDate',
  'cisaDueDate',
  'lastSeenDate',
  'slaDueDate',
  'triageDueDate',
  'remediationDueDate',
  'exceptionsStartDate',
  'exceptionsEndDate',
];

export const DUE_DATE_KEYS = [
  'slaDueDate',
  'triageDueDate',
  'remediationDueDate',
];

export const DISABLE_TOTAL_IN_COLUMNS = [
  'tags',
  'cve',
  'cwesStrings',
  ARMORCODE_GROUP_KEY,
];

export const KEYS_WITH_IDS = [
  'product',
  'subProduct',
  'team',
  'armorcodeProjects',
];

export const KEYS_NOT_TO_POPULATE_IN_STATS = [
  ...DATE_FILTER_API_SUPPORT,
  ...DUE_DATE_KEYS,
];

export const DATE_KEYS = [
  'lastOpenDate',
  'publishedDate',
  'issueResolved',
  'lastConfirmDate',
  'foundOn',
];

export const checkIfDateColumns = (column: string): boolean =>
  [...DUE_DATE_KEYS, ...CRA_DUE_DATE_KEYS, ...DATE_KEYS].indexOf(column) !== -1;

export const checkIfSLAStatusColumns = (column: string): boolean =>
  ['triageSlaStatus', 'resolutionSlaStatus', 'remediationSlaStatus'].indexOf(
    column,
  ) !== -1;

interface SLAStatusFilter {
  name: string;
  id: string | null;
}

export const SLA_STATUS_FILTERS_WITHOUT_NA: SLAStatusFilter[] = [
  { name: 'Failed', id: 'FAIL' },
  { name: 'Pass', id: 'PASS' },
  { name: 'In Progress', id: 'IN_PROGRESS' },
];

export const SLA_STATUS_FILTERS: SLAStatusFilter[] = [
  ...SLA_STATUS_FILTERS_WITHOUT_NA,
  { name: CellRenderers.VALUE_OR_NA(), id: null },
];

export const RENDER_FINDING_SLA_STATUS_FILTERS = (
  status: string | null,
): string =>
  find(
    SLA_STATUS_FILTERS,
    (slaStatus: SLAStatusFilter) => slaStatus.id === status,
  )?.name || (status as string);

// Refer to ENG-98774 for the order of finding columns if any changes made to below default hidden columns.
export const DEFAULT_HIDDEN_COLUMN_KEYS = [
  'description',
  'unsupressTime',
  'aatiScore',
  'cve',
  'scan',
  'baseScore',
  'exploitabilityScore',
  'impactScore',
  'cwesStrings',
  'foundOn',
  'lastConfirmDate',
  'lastFalsePositiveDate',
  'lastOpenDate',
  'lastAcceptRiskDate',
  'lastSuppressDate',
  'productStatus',
  'subProductVersion',
  'subProductStatuses',
  'environmentName',
  'team',
  'businessOwner',
  'securityOwner',
  'engineeringOwner',
  'supportOwner',
  'complianceOwner',
  'armorcodeProjects',
  'riskRegister',
  'armorcodeTicketStatus',
  'vendor',
  'toolId',
  'url',
  'toolSeverity',
  'toolFindingStatus',
  'toolSubStatusOriginal',
  'findingCategory',
  'keyValue',
  'componentName',
  'componentVersion',
  'componentFixVersions',
  'fixability',
  'SNYK_exploitMaturity',
  'repositoryName',
  'filePath',
  'lineNumber',
  'developer',
  'vmHostName',
  'newUrl',
  'containerImageName',
  'Twistlock_originType',
  'Twistlock_scanType',
  'ruleId',
  'policy',
  'lastUpdated',
  'issueResolved',
  'exploited',
  'XRay_Path',
  'XRay_Impacted_Artifact',
  'lastTriagedDate',
  'assetScore',
  'vmHostIpAddresses',
  'cvssVector',
  'epssScore',
  'isCISAKEV',
  'cisaDueDate',
  'lastSeenDate',
  'versionFindingsCount',
  'correlatedFindingsCount',
  'ctiScore',
  'remediationDueDate',
  'triageDueDate',
  'statusChangeReason',
  'exceptions',
  'ticketOwnerNames',
  'exceptionsId',
  'exceptionsStartDate',
  'exceptionsEndDate',
  'exceptionsReason',
  'exceptionsStatus',
  'lastModifiedDate',
  'publishedDate',
  'lastResolutionNote',
  'resolutionSlaStatus',
  'assessments',
  'correlationName',
  'remediationAction',
  'mitigation',
  'impact',
  'stepsToReproduce',
  'riskRating',
  'lastScanDate',
  'epssProbability',
  'mobbFixUrl',
  'cvssOverallScore',
  'lastControlledDateTime',
  'lastInProgressDateTime',
  'craExploitStatus',
  'craNotificationStatus',
  'craEarlyWarningDue',
  'craNotificationDue',
  'craFinalReportDue',
  'craUserDisclosureDue',
  'craUserDisclosureStatus',
  'craRemediationEvidence',
  'commentCount',
];

export const GroupBySupportedFieldsFlagNames: Record<string, string> = {
  assetType: 'RBVM_ASSETS',
  assetName: 'RBVM_ASSETS',
  riskRating: 'RISK_SCORE_3_0',
  owaspTop10_2021: 'OWASP_CWE_FILTER',
  cweTop25_2024: 'OWASP_CWE_FILTER',
  craExploitStatus: 'CRA',
  craNotificationStatus: 'CRA',
  craUserDisclosureStatus: 'CRA',
  craEarlyWarningDue: 'CRA',
  craNotificationDue: 'CRA',
  craFinalReportDue: 'CRA',
  craUserDisclosureDue: 'CRA',
};

export const GroupBySupportedFields = [
  'componentName',
  'componentFixVersions',
  'componentVersion',
  'cve',
  'cwesStrings',
  'vmHostName',
  'exploited',
  'containerImageName',
  'product',
  'scanType',
  'severity',
  'source',
  'status',
  'subProduct',
  'title',
  'slaDueDate',
  'triageDueDate',
  'remediationDueDate',
  'resolutionSlaStatus',
  'triageSlaStatus',
  'remediationSlaStatus',
  'armorcodeCategory',
  'tags',
  'isCISAKEV',
  'team',
  'assetType',
  'assetName',
  ARMORCODE_GROUP_KEY,
  'armorcodeProjects',
  'developer',
  'correlationName',
  'remediationAction',
  'repositoryName',
  'armorcodeTicket',
  'filePath',
  'riskRating',
  'owaspTop10_2021',
  'cweTop25_2024',
  'craExploitStatus',
  'craNotificationStatus',
  'craUserDisclosureStatus',
  'craEarlyWarningDue',
  'craNotificationDue',
  'craFinalReportDue',
  'craUserDisclosureDue',
  'lastOpenDate',
  'publishedDate',
  'issueResolved',
  'lastConfirmDate',
  'foundOn',
];

// there are others as well, but these are used in group by
export const PartialSearchSupportedFields = [
  'cve',
  'cwesStrings',
  'vmHostName',
  'containerImageName',
  'title',
  'correlationName',
  'repositoryName',
  'armorcodeTicket',
  'filePath',
  'remediationAction',
];

export const DEFAULT_DATE_FORMATS: Record<string, string> = {
  lastUpdated: 'relativeTime',
  cisaDueDate: 'date',
  lastSeenDate: 'date',
  lastTriagedDate: 'date',
  foundOn: 'date',
  slaDueDate: 'date',
  craEarlyWarningDue: 'date',
  craNotificationDue: 'date',
  craFinalReportDue: 'date',
  craUserDisclosureDue: 'date',
};

export const getCustomColumnTitles = (): Record<string, string> => ({
  [ARMORCODE_GROUP_KEY]: getVocabulary('group'),
  triageSlaStatus: `${getVocabulary('triage')} SLA Status`,
  remediationSlaStatus: `${getVocabulary('remediation')} SLA Status`,
  resolutionSlaStatus: `${getVocabulary('resolution')} SLA Status`,
  tags: 'Tags',
  assetType: 'Asset Type',
  assetName: 'Asset Name',
  correlationName: 'Remediation Group',
  remediationAction: 'Remediation Action',
  riskRating: 'Risk Rating',
  owaspTop10_2021: 'OWASP Top 10 - 2021',
  cweTop25_2024: 'CWE Top 25 - 2024',
});

export const CUSTOM_COLUMN_PREFIX = 'keyValue.';
